/**
 * WebAuthn Client Utilities
 * 
 * Client-side helpers for WebAuthn credential registration and authentication.
 * Uses @simplewebauthn/browser for the WebAuthn API interactions.
 */

import { startRegistration, startAuthentication } from "@simplewebauthn/browser";
import type {
  PublicKeyCredentialCreationOptionsJSON,
  PublicKeyCredentialRequestOptionsJSON,
  RegistrationResponseJSON,
  AuthenticationResponseJSON,
} from "@simplewebauthn/types";

// =============================================
// Types
// =============================================

export interface WebAuthnRegistrationResult {
  success: boolean;
  error?: string;
  credentialId?: string;
}

export interface WebAuthnAuthenticationResult {
  success: boolean;
  error?: string;
  action?: "login" | "switch" | "unlocked";
  userId?: string;
}

export interface WebAuthnCapabilities {
  /** Whether WebAuthn is supported in this browser */
  isSupported: boolean;
  /** Whether platform authenticator (biometric/PIN) is available */
  isPlatformAuthenticatorAvailable: boolean;
  /** Whether user verification (biometric) is available */
  isUserVerificationAvailable: boolean;
}

// =============================================
// Capability Detection
// =============================================

/**
 * Check if WebAuthn is supported and what capabilities are available.
 * This helps determine fallback strategies for devices without biometrics.
 */
export async function getWebAuthnCapabilities(): Promise<WebAuthnCapabilities> {
  // Check basic WebAuthn support
  const isSupported = typeof window !== "undefined" 
    && window.PublicKeyCredential !== undefined
    && typeof window.PublicKeyCredential === "function";

  if (!isSupported) {
    return {
      isSupported: false,
      isPlatformAuthenticatorAvailable: false,
      isUserVerificationAvailable: false,
    };
  }

  // Check platform authenticator availability (TouchID, FaceID, Windows Hello, etc.)
  let isPlatformAuthenticatorAvailable = false;
  try {
    isPlatformAuthenticatorAvailable = await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
  } catch (error) {
    console.warn("[WebAuthn] Failed to check platform authenticator:", error);
  }

  // Check if user verification is available (this is a more specific check)
  let isUserVerificationAvailable = false;
  try {
    // If platform authenticator is available, user verification is likely available too
    isUserVerificationAvailable = isPlatformAuthenticatorAvailable;
    
    // Additional check via conditional mediation (passkey autofill) support
    if ("isConditionalMediationAvailable" in PublicKeyCredential) {
      const conditionalAvailable = await (PublicKeyCredential as any).isConditionalMediationAvailable();
      if (conditionalAvailable) {
        isUserVerificationAvailable = true;
      }
    }
  } catch (error) {
    console.warn("[WebAuthn] Failed to check user verification:", error);
  }

  return {
    isSupported,
    isPlatformAuthenticatorAvailable,
    isUserVerificationAvailable,
  };
}

// =============================================
// Registration (Create Credential)
// =============================================

/**
 * Register a new WebAuthn credential for the current user.
 * This creates a biometric/PIN-protected credential on the device.
 * 
 * @param deviceFingerprint - Unique identifier for this device
 * @param credentialName - Optional friendly name for the credential
 * @returns Registration result
 */
export async function registerWebAuthnCredential(
  deviceFingerprint: string,
  credentialName?: string
): Promise<WebAuthnRegistrationResult> {
  try {
    // 1. Get registration challenge from server
    const challengeResponse = await fetch("/api/auth/webauthn/challenge", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        type: "registration",
        deviceFingerprint,
        credentialName,
      }),
    });

    if (!challengeResponse.ok) {
      const error = await challengeResponse.json();
      return {
        success: false,
        error: error.error || "Failed to get registration challenge",
      };
    }

    const { options, challenge } = await challengeResponse.json();

    // 2. Start browser registration (prompts biometric/PIN)
    let credential: RegistrationResponseJSON;
    try {
      credential = await startRegistration(options);
    } catch (error: any) {
      // User cancelled or biometric failed
      if (error.name === "NotAllowedError") {
        return {
          success: false,
          error: "Biometric authentication was cancelled or failed",
        };
      }
      throw error;
    }

    // 3. Verify with server and store credential
    const verifyResponse = await fetch("/api/auth/webauthn/verify", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        type: "registration",
        credential,
        expectedChallenge: challenge,
        deviceFingerprint,
        credentialName,
      }),
    });

    if (!verifyResponse.ok) {
      const error = await verifyResponse.json();
      return {
        success: false,
        error: error.error || "Failed to verify credential",
      };
    }

    const result = await verifyResponse.json();
    return {
      success: true,
      credentialId: result.credentialId,
    };
  } catch (error) {
    console.error("[WebAuthn] Registration failed:", error);
    return {
      success: false,
      error: error instanceof Error ? error.message : "Registration failed",
    };
  }
}

// =============================================
// Authentication (Use Credential)
// =============================================

/**
 * Authenticate using a WebAuthn credential.
 * This verifies the user via biometric/PIN and logs them in.
 * 
 * @param deviceFingerprint - Unique identifier for this device
 * @param userId - Optional user ID if authenticating a specific user
 * @returns Authentication result
 */
export async function authenticateWithWebAuthn(
  deviceFingerprint: string,
  userId?: string
): Promise<WebAuthnAuthenticationResult> {
  try {
    // 1. Get authentication challenge from server
    const challengeResponse = await fetch("/api/auth/webauthn/challenge", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        type: "authentication",
        userId,
        deviceFingerprint,
      }),
    });

    if (!challengeResponse.ok) {
      const error = await challengeResponse.json();
      return {
        success: false,
        error: error.error || "Failed to get authentication challenge",
      };
    }

    const { options, challenge } = await challengeResponse.json();

    // 2. Start browser authentication (prompts biometric/PIN)
    let credential: AuthenticationResponseJSON;
    try {
      credential = await startAuthentication(options);
    } catch (error: any) {
      // User cancelled or biometric failed
      if (error.name === "NotAllowedError") {
        return {
          success: false,
          error: "Biometric authentication was cancelled or failed",
        };
      }
      throw error;
    }

    // 3. Verify with server
    const verifyResponse = await fetch("/api/auth/webauthn/verify", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        type: "authentication",
        credential,
        expectedChallenge: challenge,
        deviceFingerprint,
      }),
    });

    if (!verifyResponse.ok) {
      const error = await verifyResponse.json();
      return {
        success: false,
        error: error.error || "Authentication failed",
      };
    }

    const result = await verifyResponse.json();
    return {
      success: true,
      action: result.action,
      userId: result.userId,
    };
  } catch (error) {
    console.error("[WebAuthn] Authentication failed:", error);
    return {
      success: false,
      error: error instanceof Error ? error.message : "Authentication failed",
    };
  }
}

// =============================================
// Helper to remember account after login
// =============================================

/**
 * Remember the current account on this device.
 * Call this after successful login when "Save Login" is enabled.
 * 
 * @param deviceFingerprint - Unique identifier for this device
 * @param deviceName - Optional friendly name for this device
 */
export async function rememberAccount(
  deviceFingerprint: string,
  deviceName?: string
): Promise<{ success: boolean; error?: string }> {
  try {
    const response = await fetch("/api/auth/remembered-accounts", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        deviceFingerprint,
        deviceName,
      }),
    });

    if (!response.ok) {
      const error = await response.json();
      return {
        success: false,
        error: error.error || "Failed to remember account",
      };
    }

    return { success: true };
  } catch (error) {
    console.error("[WebAuthn] Failed to remember account:", error);
    return {
      success: false,
      error: error instanceof Error ? error.message : "Failed to remember account",
    };
  }
}

/**
 * Get remembered accounts for this device.
 * 
 * @param deviceFingerprint - Unique identifier for this device
 */
export async function getRememberedAccounts(
  deviceFingerprint: string
): Promise<{
  accounts: Array<{
    userId: string;
    displayName: string | null;
    email: string | null;
    avatarUrl: string | null;
    hasPasskey: boolean;
    hasBiometric: boolean;
    lastUsedAt: string | null;
  }>;
}> {
  try {
    const response = await fetch("/api/auth/remembered-accounts", {
      headers: {
        "x-device-fingerprint": deviceFingerprint,
      },
    });

    if (!response.ok) {
      return { accounts: [] };
    }

    return await response.json();
  } catch (error) {
    console.error("[WebAuthn] Failed to get remembered accounts:", error);
    return { accounts: [] };
  }
}
